import dayjs from 'dayjs';
import { DateTime } from 'luxon';

import { ShiftListItemDto } from '@clh/api-client';
import { STANDARD_MEAL_BREAK_MINUTES } from '@clh/util';

import { calculateActualClockTimes } from './calculate-actual-clock-times';

interface ShiftTimeWorked {
    wasCalledIn: boolean;
    didNotWork: boolean;
    tookMealBreak: boolean;
    clockInTime?: dayjs.Dayjs;
    clockOutTime?: dayjs.Dayjs;
}

interface TimeCalculationResult {
    worked: number;
    onCall: number;
    billableWorked: number;
}

export const useTimeCalculations = (
    shiftTimeWorked: ShiftTimeWorked,
    selection: ShiftListItemDto
): TimeCalculationResult => {
    const calculateHours = (): { worked: number; onCall: number } => {
        if (!shiftTimeWorked.clockInTime || !shiftTimeWorked.clockOutTime) {
            return { worked: 0, onCall: 0 };
        }

        const facilityTimezone =
            selection.facility.address.timezone ?? 'America/Chicago';
        const scheduledStart = DateTime.fromJSDate(selection.startTime, {
            zone: facilityTimezone,
        });

        const { actualClockIn, actualClockOut } = calculateActualClockTimes(
            scheduledStart,
            shiftTimeWorked.clockInTime,
            shiftTimeWorked.clockOutTime,
            facilityTimezone
        );

        const workedHours = actualClockOut.diff(actualClockIn, 'hours').hours;

        if (selection.onCallShift) {
            const scheduledEnd = DateTime.fromJSDate(selection.endTime, {
                zone: facilityTimezone,
            });

            if (!shiftTimeWorked.wasCalledIn) {
                return { worked: 0, onCall: workedHours };
            }

            const onCallBefore = Math.max(
                0,
                actualClockIn.diff(scheduledStart, 'hours').hours
            );
            const onCallAfter = Math.max(
                0,
                scheduledEnd.diff(actualClockOut, 'hours').hours
            );

            return { worked: workedHours, onCall: onCallBefore + onCallAfter };
        }

        return { worked: workedHours, onCall: 0 };
    };

    const { worked, onCall } = calculateHours();

    const billableWorked =
        shiftTimeWorked.tookMealBreak && worked > 0
            ? worked - STANDARD_MEAL_BREAK_MINUTES / 60
            : worked;

    return { worked, onCall, billableWorked };
};
